import { Component, OnDestroy, OnInit } from '@angular/core';
import { HrefType } from '../../../../hdi-components/src/shared/enums/href-type.enums';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, of, Subscription, switchMap } from 'rxjs';
import {
  CaasLayout,
  CaasStage,
  CaasInpageNav,
  CaasMainSection,
  CaasComponent,
  CaasInternalLink,
  Caasnewsticker,
  CaasDisruptor,
} from '../../../../hdi-components/src/shared/interfaces/caas/caas.interface';
import { MenuItem } from '../../../../hdi-components/src/shared/interfaces/caas/nav.interface';
import { CaasContentService } from '../../services/caas-content.service';
import { ImageService } from '../../services/image.service';
import {
  PodcastItems,
  PodcastPlatforms,
  TextBlockAlign,
  TextBlockTitleVariant,
} from 'hdi-components/source';
import { ImageMetaData, ModalType, News } from 'hdi-components';
import { ModalVideoService } from '../../services/modal-video.service';
import { SafeResourceUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-home-page',
  templateUrl: './home-page.component.html',
  styleUrl: './home-page.component.scss',
})
export class HomePageComponent implements OnDestroy, OnInit {
  HrefType = HrefType;
  TextBlockTitleVariant = TextBlockTitleVariant;
  TextBlockAlign = TextBlockAlign;
  images: string[] = [];
  layouts?: CaasLayout[];
  pageCaasSubscription$!: Subscription;
  stage?: CaasStage;
  inpageNav?: CaasInpageNav;
  mainSections?: CaasMainSection[];
  videoId?: string;
  videoSrc?: SafeResourceUrl;
  showModal = false;
  podcastData: { podcasts: PodcastItems[]; platforms: PodcastPlatforms[] } = {
    podcasts: [],
    platforms: [],
  };
  newsComponent: CaasComponent | undefined;
  news: News[] = [];
  newsFilters: { filter: string; categories: string[]; identifier: string }[] =
    [];
  filteredNews: News[] = [];
  imagesMetaData: ImageMetaData[] = [];
  tags: { name: string; identifier: string }[] = [];
  alternateBackground: ('white' | 'grey')[] = [];
  inpageMenu: MenuItem[] = [];
  events: CaasLayout[] = [];
  ModalType = ModalType;
  newsticker?: Caasnewsticker;
  breadcrumbs: { title: string; url?: string }[] = [];
  alert?: CaasDisruptor;
  public paddingTop = false;
  public paddingBlock = false;
  public paddingBottom = false;
  constructor(
    private caasContentService: CaasContentService,
    private routes: ActivatedRoute,
    private imageServices: ImageService,
    private modalVideoService: ModalVideoService
  ) {
    this.pageCaasSubscription$ = this.routes.data
      .pipe(
        switchMap((data) => {
          if (data?.['pageIdentifier']) {
            return forkJoin({
              content: this.caasContentService.getContent(
                data['pageIdentifier']
              ),
              podcastData: this.caasContentService.getPodcastData(),
              news: this.caasContentService.getNews(),
              newsFilters: this.caasContentService.getNewsCategories(),
              events: this.caasContentService.getEvents(),
              imagesMetaData: this.caasContentService.getImagesMetaData(),
              tags: this.caasContentService.getTags(),
            });
          } else {
            return of({
              content: [],
              podcastData: { podcasts: [], platforms: [] },
              news: [],
              newsFilters: [],
              events: [],
              imagesMetaData: [],
              tags: [],
            });
          }
        })
      )
      .subscribe(
        ({ content, podcastData, news, newsFilters, events, tags }) => {
          // console.log('CAAS Content', content);
          this.layouts = content;
          // Check if stage layout exists
          const stageLayout = content.find((layout) => layout.name === 'stage');
          // console.log('Stage Layout', stageLayout);
          if (
            stageLayout &&
            stageLayout.children &&
            stageLayout.children.length
          ) {
            this.stage = stageLayout.children.find((child) => {
              return child.template?.uid === 'st_stage_home';
            }) as CaasStage;
            // console.log('Stage home', this.stage);
            this.imageServices.replaceStageImages(this.stage);
          }

          // Check if inpageNav layout exists
          const inpageNavLayout = content.find(
            (layout) => layout.name === 'inpagenav'
          );
          if (inpageNavLayout && inpageNavLayout.children) {
            this.inpageNav = inpageNavLayout.children[0] as CaasInpageNav;
          }

          // Check if newsticker/newsticker exists
          const disruptor = content.find(
            (layout) => layout.name === 'disruptor' && layout.children?.length
          );
          if (disruptor && disruptor.children) {
            this.newsticker = disruptor.children.find(
              (child) => child.template.uid === 'st_ticker'
            ) as Caasnewsticker;

            this.alert = disruptor.children.find(
              (child) => child.template.uid === 'st_disruptor'
            ) as CaasDisruptor;
          }

          // Main sections and handling the news component
          this.mainSections = (
            content.find((layout) => layout.name === 'main')
              ?.children as CaasMainSection[]
          )?.filter((section) => {
            if (
              section?.formData?.st_items?.value[0]?.template?.uid ===
                'st_news' &&
              section?.formData?.st_items?.value[0]?.formData?.st_layout?.value
                ?.identifier === 'hub'
            ) {
              this.newsComponent = section?.formData?.st_items?.value[0];
            }
            return (
              section?.formData?.st_items?.value[0]?.template?.uid !==
                'st_news' ||
              section?.formData?.st_items?.value[0]?.formData?.st_layout?.value
                ?.identifier !== 'hub'
            );
          });
          if (this.mainSections) {
            this.imageServices.replaceMainImages(this.mainSections);
            // console.log('Main content:', this.mainSections);
          }
          this.podcastData = podcastData;
          this.news = news;
          this.tags = tags;
          this.newsFilters = newsFilters;
          this.getAlternateBackground();
          this.inpageMenu = this.getInpageNav();

          if (this.news) {
            this.imageServices.replaceNewsImages(
              this.filteredNews.length > 0 ? this.filteredNews : this.news
            );
          }

          if (this.podcastData) {
            this.imageServices.replacePodcastsImages(this.podcastData.podcasts);
          }
          this.events = events;
        }
      );
  }

  ngOnInit() {
    this.openVideoModalByQueryParams();
  }

  updateNews(filteredNews: News[]): void {
    this.filteredNews = filteredNews;
  }

  getInternalUrl(internalLinkValue: CaasInternalLink['value']): string {
    if (internalLinkValue && internalLinkValue.uid === 'startseite') {
      return '/';
    } else {
      return '/404';
    }
  }

  getInpageNav(): MenuItem[] {
    return (this.mainSections ?? []).reduce((acc, section) => {
      if (
        section.formData.st_anchor_name?.value &&
        section.formData.st_inpagenav?.value
      ) {
        acc.push({
          title:
            section.formData.st_inpagenav.value.formData.lt_link_label.value ??
            '',
          url: section.formData.st_anchor_name.value,
          hoverText:
            section.formData.st_inpagenav.value.formData.lt_link_title?.value ??
            '',
        });
      }
      if (
        section.formData.st_items &&
        section.formData.st_items.value &&
        section.formData.st_items.value.length > 0
      ) {
        section.formData.st_items.value.forEach((item) => {
          if (
            item.formData.st_anchor_name?.value &&
            item.formData.st_inpagenav?.value
          ) {
            acc.push({
              title:
                item.formData.st_inpagenav.value.formData.lt_link_label.value ??
                '',
              url: item.formData.st_anchor_name.value,
              hoverText:
                item.formData.st_inpagenav.value.formData.lt_link_title
                  ?.value ?? '',
            });
          }
        });
      }
      return acc;
    }, [] as MenuItem[]);
  }

  openVideoModalByQueryParams(): void {
    const { showModal, videoId, videoSrc } =
      this.modalVideoService.openModalVideo();
    this.showModal = showModal;
    this.videoId = videoId;
    this.videoSrc = videoSrc;
  }

  getWelcomeSectionContent(stage: CaasStage) {
    return stage.formData.st_header?.value;
  }

  getAlternateBackground() {
    let isWhite = (index: number) => index % 2 !== 0;
    this.mainSections?.forEach((section, index) => {
      if (section.formData.st_background_reset?.value) {
        this.alternateBackground.push('white');
        if (!isWhite(index) && !isWhite(0)) {
          isWhite = (index: number) => index % 2 === 0;
        } else {
          isWhite = (index: number) => index % 2 !== 0;
        }
      } else if (isWhite(index)) {
        this.alternateBackground.push('white');
      } else {
        this.alternateBackground.push('grey');
      }
    });
  }

  ngOnDestroy(): void {
    this.pageCaasSubscription$.unsubscribe();
  }
}
